import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { authService } from "@/services/auth-service";
import { normalizePhoneNumber, isValidPhoneNumber } from "@/utils/phoneUtils";

const AuthForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [showEmailOtpField, setShowEmailOtpField] = useState(false);
  const [showPhoneOtpField, setShowPhoneOtpField] = useState(false);
  const [emailMethodId, setEmailMethodId] = useState<string | null>(null);
  const [phoneMethodId, setPhoneMethodId] = useState<string | null>(null);
  const [authMethod, setAuthMethod] = useState("email");
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otp) {
      toast({
        title: "Verification code required",
        description: "Please enter the verification code",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (authMethod === "email" && emailMethodId) {
        await authService.verifyCode(email, true, otp, emailMethodId);
      } else if (authMethod === "phone" && phoneMethodId) {
        const normalizedPhone = normalizePhoneNumber(phone);
        await authService.verifyCode(normalizedPhone, false, otp, phoneMethodId);
      }
      
      // Success notification
      toast({
        title: "Authentication successful",
        description: "Welcome to LevAI!",
      });
      
      navigate("/voice-record");
    } catch (error: any) {
      toast({
        title: "Authentication failed",
        description: error.message || "Invalid verification code",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmailOtp = async () => {
    if (!email || !email.includes('@')) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await authService.sendVerificationCode(email, true);
      setEmailMethodId(response.method_id);
      setShowEmailOtpField(true);
      
      toast({
        title: "Verification code sent",
        description: `A verification code has been sent to ${email}`,
      });
    } catch (error: any) {
      toast({
        title: "Failed to send code",
        description: error.message || "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendPhoneOtp = async () => {
    if (!phone) {
      toast({
        title: "Phone number required",
        description: "Please enter a phone number",
        variant: "destructive",
      });
      return;
    }
    
    const normalizedPhone = normalizePhoneNumber(phone);
    if (!isValidPhoneNumber(normalizedPhone)) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid phone number",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await authService.sendVerificationCode(normalizedPhone, false);
      setPhoneMethodId(response.method_id);
      setShowPhoneOtpField(true);
      
      toast({
        title: "Verification code sent",
        description: `A verification code has been sent to ${phone}`,
      });
    } catch (error: any) {
      toast({
        title: "Failed to send code",
        description: error.message || "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardDescription className="text-center">
          Sign in or sign up
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="email" onValueChange={setAuthMethod}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="email">Email</TabsTrigger>
            <TabsTrigger value="phone">Phone</TabsTrigger>
          </TabsList>

          <TabsContent value="email">
            <form onSubmit={handleLogin}>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Input
                    id="email"
                    placeholder="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                
                {!showEmailOtpField ? (
                  <Button 
                    type="button" 
                    className="w-full bg-brand-500 hover:bg-brand-600"
                    onClick={handleSendEmailOtp}
                    disabled={isLoading}
                  >
                    {isLoading ? "Sending..." : "Send Verification Code"}
                  </Button>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Input
                        id="email-otp"
                        placeholder="Enter Verification Code"
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        required
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-brand-500 hover:bg-brand-600"
                      disabled={isLoading}
                    >
                      {isLoading ? "Verifying..." : "Verify & Sign In"}
                    </Button>
                  </>
                )}
              </div>
            </form>
          </TabsContent>

          <TabsContent value="phone">
            <form onSubmit={handleLogin}>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Input
                    id="phone"
                    placeholder="Phone Number"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                  />
                </div>
                
                {!showPhoneOtpField ? (
                  <Button 
                    type="button" 
                    className="w-full bg-brand-500 hover:bg-brand-600"
                    onClick={handleSendPhoneOtp}
                    disabled={isLoading}
                  >
                    {isLoading ? "Sending..." : "Send Verification Code"}
                  </Button>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Input
                        id="phone-otp"
                        placeholder="Enter Verification Code"
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        required
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-brand-500 hover:bg-brand-600"
                      disabled={isLoading}
                    >
                      {isLoading ? "Verifying..." : "Verify & Sign In"}
                    </Button>
                  </>
                )}
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default AuthForm;
